import moment from "moment";

const METHOD = Object.freeze({
  Cash: "Cash",
  "Bank Transfer": "Bank transfer",
  Card: "Card",
  "Card - BNPL": "Card BNPL",
  Raast: "Raast",
});

const PAYMENT = Object.freeze({
  PENDING: "Awaiting payment",
  APPROVED: "Paid",
  SETTLED: "Settled",
  SETTLE_PENDING: "Settlement pending",
  CREATED: "Initiated",
  DECLINED: "Declined",
  EXPIRED: "Expired",
});

const numeric = (value) =>
  value === null || value === "" || Number.isNaN(Number(value))
    ? null
    : Number(value);

const readable = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/^./, (letter) => letter.toUpperCase());

export const formatMoney = (value) =>
  `PKR ${Number(value || 0).toLocaleString("en-PK", {
    maximumFractionDigits: 2,
  })}`;

export const formatPaymentMethod = (value) =>
  METHOD[value] || readable(value) || "Not available";

export const formatPaymentStatus = (value, order) => {
  if (value === 'APPROVED' && order) {
    const received = Number(order.amount_received || 0);
    const receivable = Number(order.amount_receivable || 0);
    if (received > 0 && receivable > 0 && received < receivable) {
      return `Advance Rs. ${received.toLocaleString('en-PK')}`;
    }
  }
  return PAYMENT[value] || readable(value) || "Not available";
};

export const formatPaymentSummary = (order) =>
  `${formatPaymentMethod(order?.trx_method)} · ${formatPaymentStatus(
    order?.trx_status, order
  )}`;

const parseTimestamp = (value) => {
  if (value === undefined || value === null || value === "") return null;

  // The API stores created_at/updated_at as PKT wall-clock times, but mysql2
  // returns them as JS Date objects interpreted as UTC, which Express then
  // serializes as ISO strings with a "Z" suffix. Parsing those with moment
  // would shift the displayed time by the local UTC offset (+5h for PKT).
  // Stripping the trailing timezone indicator makes moment treat the value
  // as local time, which matches the PKT wall-clock time stored in the DB.
  const normalized =
    typeof value === "string"
      ? value.replace(/([Zz]|[+-]\d{2}:?\d{2})$/, "")
      : value;

  const parsed =
    typeof normalized === "string"
      ? moment(
          normalized,
          [moment.ISO_8601, moment.RFC_2822, "YYYY-MM-DD HH:mm:ss"],
          true
        )
      : moment(normalized);
  return parsed.isValid() ? parsed : null;
};

export const formatExactDate = (value) =>
  parseTimestamp(value)?.format("DD MMM YYYY, h:mm A") || "";

export const formatRelativeUpdate = (value) => {
  const timestamp = parseTimestamp(value);
  return timestamp ? `Updated ${timestamp.fromNow()}` : "";
};

export const getNonZeroItemFacts = (item) =>
  [
    Number(item?.online_price) !== Number(item?.original_price)
      ? { label: "Online price", value: numeric(item?.online_price) }
      : null,
    { label: "Item discount", value: numeric(item?.discount_value) },
    {
      label: "Bank transfer discount",
      value: numeric(item?.order_item_bank_transfer_discount),
    },
    { label: "Voucher discount", value: numeric(item?.voucher_discount) },
    { label: "Delivery", value: numeric(item?.delivery_charges) },
  ].filter((fact) => fact?.value > 0);

export const getOrderFinancials = (order) => {
  const items = (order?.order_sources || []).flatMap(
    (source) => source.order_items || []
  );
  const listingDiscount = items.reduce(
    (sum, item) =>
      sum +
      Number(item.discount_value || 0) +
      Number(item.order_item_bank_transfer_discount || 0) +
      Number(item.voucher_discount || 0),
    0
  );

  return {
    subtotal: numeric(order?.sub_total),
    discount: numeric(order?.discount_value) ?? listingDiscount,
    delivery: numeric(order?.delivery_amount),
    total: numeric(order?.grand_total) ?? 0,
    advancePaid: numeric(order?.amount_received),
    amountReceivable: numeric(order?.amount_receivable),
  };
};
